import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import {
  NewsCard, BlogCard, OrgCard, OpportunityCard, EventCard, InitiativeCard, CardSkeleton,
} from "@/components/Cards";

export function PageBanner({ label, title, desc, image }) {
  return (
    <section className="relative overflow-hidden border-b border-dir-gold/15 bg-dir-deep pt-[72px]">
      {image && <img src={image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-15" />}
      <div className="absolute inset-0 section-noise" />
      <div className="relative mx-auto max-w-[1400px] px-4 py-20 sm:px-6 lg:px-10">
        {label && <div className="text-xs uppercase tracking-[0.25em] font-semibold text-dir-gold">{label}</div>}
        <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight text-dir-text sm:text-5xl">{title}</h1>
        {desc && <p className="mt-4 max-w-2xl text-base leading-relaxed text-dir-muted">{desc}</p>}
      </div>
    </section>
  );
}

function Listing({ endpoint, banner, CardComp, categories = [], gridCols = "sm:grid-cols-2 lg:grid-cols-3", testId }) {
  const [items, setItems] = useState(null);
  const [params, setParams] = useSearchParams();
  const active = params.get("category") || "All";

  useEffect(() => {
    setItems(null);
    const url = active === "All" ? endpoint : `${endpoint}${endpoint.includes("?") ? "&" : "?"}category=${encodeURIComponent(active)}`;
    api.get(url).then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, [endpoint, active]);

  return (
    <div className="min-h-screen bg-dir-bg" data-testid={testId}>
      <PageBanner {...banner} />
      <div className="mx-auto max-w-[1400px] px-4 py-14 sm:px-6 lg:px-10">
        {categories.length > 0 && (
          <div className="mb-10 flex flex-wrap gap-2" data-testid="category-filters">
            {["All", ...categories].map((c) => (
              <button key={c} data-testid={`filter-${c.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => setParams(c === "All" ? {} : { category: c })}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${active === c ? "bg-dir-gold text-dir-deep" : "border border-dir-gold/25 text-dir-muted hover:text-dir-text hover:border-dir-gold/50"}`}>
                {c}
              </button>
            ))}
          </div>
        )}
        {!items ? (
          <div className={`grid gap-6 ${gridCols}`}>{[0, 1, 2, 3, 4, 5].map((i) => <CardSkeleton key={i} />)}</div>
        ) : items.length === 0 ? (
          <p className="py-20 text-center text-dir-muted">No results found.</p>
        ) : (
          <div className={`grid gap-6 ${gridCols}`}>{items.map((it) => <CardComp key={it.id} item={it} />)}</div>
        )}
      </div>
    </div>
  );
}

export function NewsPage() {
  return <Listing testId="news-page" endpoint="/news" CardComp={NewsCard}
    categories={["DIR Nation", "Community", "Diaspora", "Development", "Business", "Investment", "Education", "Technology", "Culture", "Youth", "Women", "International", "Partnerships"]}
    banner={{ label: "Newsroom", title: "News & Global Knowledge", desc: "The latest from DIR Nation communities, institutions and diaspora networks worldwide." }} />;
}
export function ArticlesPage() {
  const ArticleCard = ({ item }) => <Link to={`/articles/${item.slug}`} className="group overflow-hidden rounded-2xl border border-dir-gold/15 bg-dir-card transition-all hover:border-dir-gold/50">{item.featured_image && <img src={item.featured_image} alt={item.featured_image_alt || item.title} className="h-52 w-full object-cover" />}<div className="p-6"><div className="text-xs uppercase tracking-wider text-dir-gold">{item.category || "Article"}</div><h2 className="mt-2 text-xl font-semibold text-dir-text group-hover:text-dir-gold">{item.title}</h2><p className="mt-3 line-clamp-3 text-sm text-dir-muted">{item.excerpt}</p></div></Link>;
  return <Listing testId="articles-page" endpoint="/articles" CardComp={ArticleCard}
    categories={["Community", "Leadership", "Development", "Culture", "Business", "Technology", "Platform"]}
    banner={{ label: "DIR Nation Publishing", title: "Articles & Features", desc: "In-depth stories, analysis and perspectives from across the global DIR Nation community." }} />;
}
export function BlogsPage() {
  return <Listing testId="blogs-page" endpoint="/blogs" CardComp={BlogCard}
    categories={["Leadership", "History", "Heritage", "Community", "Diaspora", "Development", "Business", "Entrepreneurship", "Technology", "Education", "Opinion", "Future Generations"]}
    banner={{ label: "Insights & Blogs", title: "Perspectives & Insights", desc: "Thought leadership, heritage and community perspectives from across the DIR world." }} />;
}
export function OrganizationsPage() {
  return <Listing testId="organizations-page" endpoint="/organizations" CardComp={OrgCard}
    categories={["Community", "Youth", "Business", "Women", "Culture", "Education", "Development"]}
    banner={{ label: "Global Directory", title: "Organizations & Institutions", desc: "Discover NGOs, associations, businesses, foundations and diaspora networks across the globe." }} />;
}
export function OpportunitiesPage() {
  return <Listing testId="opportunities-page" endpoint="/opportunities" CardComp={OpportunityCard}
    categories={["Jobs", "Scholarships", "Internships", "Business", "Investment", "Grants", "Fellowships", "Volunteering", "Conferences", "Training"]}
    banner={{ label: "Opportunities Hub", title: "Opportunities", desc: "Jobs, scholarships, grants, fellowships and investment opportunities for our global community." }} />;
}
export function EventsPage() {
  return <Listing testId="events-page" endpoint="/events" CardComp={EventCard} gridCols="sm:grid-cols-2"
    categories={["Conference", "Webinar", "Cultural", "Community"]}
    banner={{ label: "Events & Summits", title: "Events Calendar", desc: "Conferences, webinars, cultural gatherings and summits connecting our global community." }} />;
}
export function InitiativesPage() {
  return <Listing testId="initiatives-page" endpoint="/initiatives" CardComp={InitiativeCard} gridCols="sm:grid-cols-2 lg:grid-cols-4"
    categories={["Education", "Economic Development", "Youth Empowerment", "Cultural Preservation", "Community Development", "Humanitarian Initiatives"]}
    banner={{ label: "Building Our Future Together", title: "Initiatives & Projects", desc: "Community-led programs strengthening education, development, culture and opportunity." }} />;
}
